import { Chess } from 'chess.js';

import { components } from '@features/shared/api/ws';

import { IClock, IGame, IMove } from '../types/game';

const RESULT_MAP = {
  W: 'WhiteWin',
  B: 'BlackWin',
  D: 'Draw',
  C: 'NotPlayed',
} as const;
const LOSS_REASON_MAP = { F: 'NoShow', B: 'Ban', R: 'Removed' } as const;
const SIDE_MAP = { W: 'White', B: 'Black' } as const;
const EVENT_TYPE_MAP = {
  D: 'DrawRequest',
  DA: 'DrawAccept',
  DR: 'DrawDecline',
  RE: 'Surrender',
  VC: 'ClaimVictory',
} as const;

export function reduceGame(game: IGame, event: components['schemas']['IWs_Game_Events']): IGame {
  if (event.t === 'time') return game;

  if (event.t === 'B') {
    return event.e.reduce(reduceGame, game);
  }

  if (game.seq >= event.sq) {
    return game;
  }

  if (game.seq !== event.sq - 1) {
    throw new Error(
      `Can't apply game event to snapshot. Sequence mismatch. Snaphot: ${game.seq}, event: ${event.sq}`,
    );
  }

  switch (event.t) {
    case 'N':
      return game;
    case 'S':
      return reduceStart(game, event);
    case 'M':
      return reduceMove(game, event);
    case 'G':
      return reduceGracePeriod(game, event);
    case 'F':
      return reduceFinish(game, event);
    case 'E':
      return reduceOther(game, event);
    default:
      return {
        ...game,
        timestampMs: (event as { ts?: number })?.ts ?? game.timestampMs,
        seq: (event as { sq?: number })?.sq ?? game.seq,
      };
  }
}

/*
    ____,-------------------------------,____
    \   |           Reducers           |   /
    /___|-------------------------------|___\
*/

// Начало игры
function reduceStart(game: IGame, e: components['schemas']['IStream_Game_Start']): IGame {
  return {
    ...game,
    status: 'InProgress',
    result: 'InProgress',
    clock: createClock(e.ck, e.sck, e.ts),
    timestampMs: e.ts,
    seq: e.sq,
  };
}

// Ход
function reduceMove(game: IGame, e: components['schemas']['IStream_Game_Move']): IGame {
  const { from, to, promotion } = parseUciMove(e.mv);
  const chess = new Chess(game.currentFen, { enable960: true });
  const result = chess.move({ from, to, promotion });

  const side = game.nextMoveBy;
  const move: IMove = {
    side,
    from,
    to,
    promotedTo: promotion?.toUpperCase() ?? null,
    san: result.san,
    fenBefore: game.currentFen,
    fenAfter: result.after,
    clockAfter: createClock(e.ck, e.sck, e.ts),
    timestampMs: e.ts,
  };

  return {
    ...game,
    moves: [...game.moves, move],
    activeDrawRequest: undefined,
    currentFen: result.after,
    clock: createClock(e.ck, e.sck, e.ts),
    nextMoveBy: side === 'White' ? 'Black' : 'White',
    timestampMs: e.ts,
    seq: e.sq,
  };
}

// Отсрочка после потери соединения
function reduceGracePeriod(
  game: IGame,
  e: components['schemas']['IStream_Game_GracePeriod'],
): IGame {
  // eslint-disable-next-line eqeqeq
  const gracePeriodMs = e.gp != null ? e.ts + e.gp * 1000 : null;
  const side = SIDE_MAP[e.s] === 'White' ? 'white' : 'black';

  return {
    ...game,
    [side]: {
      ...game[side],
      offlineGracePeriodTillTimestampMs: gracePeriodMs,
    },
    timestampMs: e.ts,
    seq: e.sq,
  };
}

// Завершение игры
function reduceFinish(game: IGame, e: components['schemas']['IStream_Game_Finish']): IGame {
  return {
    ...game,
    activeDrawRequest: undefined,
    status: 'Finished',
    result: RESULT_MAP[e.rs],
    lossReason: e.ls ? LOSS_REASON_MAP[e.ls] : undefined,
    white: { ...game.white, ratingChange: e.rc[0]! },
    black: { ...game.black, ratingChange: e.rc[1]! },
    clock: createClock(e.ck, undefined, e.ts),
    timestampMs: e.ts,
    finishTimestampMs: e.ts,
    seq: e.sq,
  };
}

// Другие события
function reduceOther(game: IGame, e: components['schemas']['IStream_Game_Other']): IGame {
  const gameEvent = {
    eventType: EVENT_TYPE_MAP[e.et],
    triggeredBySide: SIDE_MAP[e.es],
    fullMoveNumber: game.moves.length,
    timestampMs: e.ts,
  };
  const gameEvents = [...(game.gameEvents ?? []), gameEvent];

  switch (e.et) {
    case 'D':
      return {
        ...game,
        activeDrawRequest: { triggeredBySide: SIDE_MAP[e.es] },
        white: { ...game.white, drawRequestsLeft: e.dc[0]! },
        black: { ...game.black, drawRequestsLeft: e.dc[1]! },
        gameEvents,
        timestampMs: e.ts,
        seq: e.sq,
      };
    case 'DA':
    case 'DR':
      return {
        ...game,
        activeDrawRequest: undefined,
        gameEvents,
        timestampMs: e.ts,
        seq: e.sq,
      };
    case 'RE':
    case 'VC':
      return { ...game, gameEvents, timestampMs: e.ts, seq: e.sq };
    default:
      return {
        ...game,
        timestampMs: e?.ts ?? game.timestampMs,
        seq: e?.sq ?? game.seq,
      };
  }
}

/*
    ____,-------------------------------,____
    \   |           Helpers            |   /
    /___|-------------------------------|___\
*/

function parseUciMove(uci: string) {
  return {
    from: uci.slice(0, 2),
    to: uci.slice(2, 4),
    promotion: uci.length > 4 ? (uci[4]!.toLowerCase() as 'q' | 'r' | 'b' | 'n') : undefined,
  };
}

function createClock(ck: number[], sck: number[] | undefined, timestampMs: number): IClock {
  return {
    whiteLeftMs: ck[0]!,
    blackLeftMs: ck[1]!,
    whiteStartLeftMs: sck?.[0],
    blackStartLeftMs: sck?.[1],
    timestampMs,
  };
}
